import { useMemo } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import { Select, TextField, NumbersOnly, FieldData } from 'vopay';

import { PaymentFieldDefault } from './types';

export function useInteracEtransferFields(
  type: 'Pay' | 'Collect',
  autoDepositEnabled: boolean = false,
): FieldData<PaymentFieldDefault>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'UseTransactionCreationFields' });
  return useMemo(() => {
    return [
      {
        label: () => t('Fields.etransferConnectionType.label'),
        fieldName: 'EtransferConnectionType',
        getInputState: () => 'required',
        showField: (values) => values.PaymentSelectionMethod === 'email',
        validate: () => Yup.string().required(),
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return (
            <Select
              {...fieldData}
              version="2"
              options={[
                { value: 'email', label: t('etransferConnectionType.email') },
                { value: 'phone', label: t('etransferConnectionType.phone') },
              ]}
            />
          );
        },
      },
      {
        label: () => t('Fields.recipientEmailAddress.label'),
        fieldName: 'RecipientEmailAddress',
        getInputState: () => 'required',
        showField: (values) =>
          values.EtransferConnectionType === 'email' &&
          values.PaymentSelectionMethod === 'email' &&
          values.ProvideOrRequestPaymentInformation === 'provide',
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField {...fieldData} variant="outlineShadow" />;
        },
        validate: () => Yup.string().email().required(),
      },
      {
        label: () => t('Fields.recipientPhoneNumber.label'),
        fieldName: 'RecipientPhoneNumber',
        getInputState: () => 'required',
        showField: (values) =>
          values.EtransferConnectionType === 'phone' &&
          values.PaymentSelectionMethod === 'email' &&
          values.ProvideOrRequestPaymentInformation === 'provide',
        serializeField: (values) => NumbersOnly(values.RecipientPhoneNumber || ''),
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField {...fieldData} variant="outlineShadow" preset="tel" />;
        },
        validate: () => Yup.string().required(),
      },
      {
        label: () => t('Fields.question.label'),
        fieldName: 'Question',
        getInputState: () => (type === 'Pay' ? 'required' : 'optional'),
        showField: (values) =>
          !autoDepositEnabled &&
          values.PaymentSelectionMethod === 'email' &&
          values.ProvideOrRequestPaymentInformation === 'provide',
        validate: () => (type === 'Pay' ? Yup.string().required() : Yup.string()),
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField variant="outlineShadow" {...fieldData} />;
        },
      },
      {
        label: () => t('Fields.answer.label'),
        fieldName: 'Answer',
        getInputState: () => (type === 'Pay' ? 'required' : 'optional'),
        showField: (values) =>
          !autoDepositEnabled &&
          values.PaymentSelectionMethod === 'email' &&
          values.ProvideOrRequestPaymentInformation === 'provide',
        validate: () => (type === 'Pay' ? Yup.string().required() : Yup.string()),
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField variant="outlineShadow" {...fieldData} />;
        },
      },
    ];
  }, [type, autoDepositEnabled]);
}
